import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { Card, Form, Input, Select, Button, Space, message, Checkbox, Alert } from 'antd'
import { adminUsersApi } from '@/api/admin-users'
import type { CreateAdminUserInput } from '@/types/admin-user'
import { getApiErrorMessage } from '@/utils/error'

export const AdminUserCreate = () => {
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const [sendEmail, setSendEmail] = useState(false)

  const createMutation = useMutation({
    mutationFn: adminUsersApi.createAdminUser,
    onSuccess: () => {
      message.success('Admin user created successfully')
      navigate('/admin/users')
    },
    onError: (error: unknown) => {
      message.error(getApiErrorMessage(error, 'Failed to create admin user'))
    },
  })

  const onFinish = (values: CreateAdminUserInput) => {
    createMutation.mutate({
      ...values,
      send_email: sendEmail,
    })
  }

  return (
    <Card
      title="Create Admin User"
      extra={<Button onClick={() => navigate('/admin/users')}>Cancel</Button>}
    >
      <Alert
        message="User Creation"
        description={
          sendEmail
            ? 'The user will receive a welcome email with instructions to set their password.'
            : 'You must provide a temporary password. The user will be required to change it on first login.'
        }
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Form form={form} layout="vertical" onFinish={onFinish} initialValues={{ send_email: false }}>
        <Form.Item
          label="Email"
          name="email"
          rules={[
            { required: true, message: 'Email is required' },
            { type: 'email', message: 'Please enter a valid email' },
          ]}
        >
          <Input placeholder="user@example.com" />
        </Form.Item>

        <Form.Item
          label="First Name"
          name="given_name"
          rules={[{ required: true, message: 'First name is required' }]}
        >
          <Input placeholder="John" />
        </Form.Item>

        <Form.Item
          label="Last Name"
          name="family_name"
          rules={[{ required: true, message: 'Last name is required' }]}
        >
          <Input placeholder="Doe" />
        </Form.Item>

        <Form.Item
          label="Role"
          name="role"
          rules={[{ required: true, message: 'Role is required' }]}
        >
          <Select
            placeholder="Select role"
            options={[
              { label: 'Admin - Full access', value: 'ADMIN' },
              { label: 'Support - Limited access', value: 'SUPPORT' },
              { label: 'Viewer - Read-only access', value: 'VIEWER' },
            ]}
          />
        </Form.Item>

        <Form.Item>
          <Checkbox checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)}>
            Send welcome email (user will set their own password)
          </Checkbox>
        </Form.Item>

        {!sendEmail && (
          <Form.Item
            label="Temporary Password"
            name="temporary_password"
            rules={[
              { required: true, message: 'Password is required when not sending email' },
              { min: 12, message: 'Password must be at least 12 characters' },
              {
                pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/,
                message:
                  'Password must contain uppercase, lowercase, number, and special character',
              },
            ]}
            help="Must be at least 12 characters with uppercase, lowercase, number, and special character"
          >
            <Input.Password placeholder="Enter temporary password" />
          </Form.Item>
        )}

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={createMutation.isPending}>
              Create Admin User
            </Button>
            <Button onClick={() => navigate('/admin/users')}>Cancel</Button>
          </Space>
        </Form.Item>
      </Form>
    </Card>
  )
}
